const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];

export function wordCount(text) {
  return String(text).trim().split(/\s+/).filter(Boolean).length;
}

function cleanLines(raw) {
  return raw
    .split('\n')
    .map((line) => line.replace(/^>\s?/, '').trim())
    .filter((line) => line && !line.startsWith('## ') && !line.startsWith('- '));
}

function extractField(part, label) {
  const match = part.match(new RegExp(`- ${label}:\\s*\\n+([\\s\\S]*?)(?=\\n- |\\n## |$)`));
  if (!match) return '';
  return cleanLines(match[1]).join(' ');
}

function normalizeVisual(raw) {
  return raw
    .replace(/\s*\[\[QUEBRA\]\]\s*/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .trim();
}

function sliceVisual(visual, spokenParts) {
  if (spokenParts.length === 1) return [visual];

  const collapsed = visual.replace(/\s+/g, ' ').trim();
  const indexes = spokenParts.map((part) => collapsed.indexOf(part.slice(0, 24).trim()));
  if (indexes.some((index) => index < 0)) return spokenParts.slice();

  return spokenParts.map((_, index) => {
    const from = index === 0 ? 0 : indexes[index];
    const to = index === spokenParts.length - 1 ? collapsed.length : indexes[index + 1];
    return collapsed.slice(from, to).trim();
  });
}

function parseBlocks(section) {
  return section.split(/\n## Bloco /).slice(1).map((part) => {
    const header = part.split('\n')[0];
    const role = header.includes('destaque') ? 'highlight' : 'body';
    const visual = normalizeVisual(extractField(part, 'Título textual'));
    const spokenRaw = extractField(part, 'Texto para leitura').replace(/\s+/g, ' ').trim();
    const spokenParts = spokenRaw.split('[[PROC]]').map((piece) => piece.trim()).filter(Boolean);
    const visuals = sliceVisual(visual, spokenParts);

    return {
      role,
      fragments: spokenParts.map((spoken, index) => ({
        spoken,
        visual: visuals[index] || spoken,
        preset: role === 'highlight' ? 'blurry' : 'linear',
      })),
    };
  }).filter((block) => block.fragments.length);
}

function parseChapter(section, index) {
  const titlePart = section.split('## Bloco')[0];
  const titleLine = (titlePart.match(/- Título textual:\s*(.+)/) || [])[1] || '';
  const meta = {
    weekday: (section.match(/- Dia da semana:\s*(.+)/) || [])[1]?.trim() || '',
    day: (section.match(/- Dia:\s*(.+)/) || [])[1]?.trim() || '',
    month: (section.match(/- Mês:\s*(.+)/) || [])[1]?.trim() || '',
    year: (section.match(/- Ano:\s*(.+)/) || [])[1]?.trim() || '',
  };

  return {
    key: `chapter${index + 1}`,
    label: ROMAN[index],
    title: normalizeVisual(titleLine),
    meta,
    blocks: parseBlocks(section),
    afterword: section.includes('## Assinatura') ? { text: 'TUA' } : null,
  };
}

function parseIntro(markdown) {
  const section = markdown.split('\n# ')[2] || '';
  const titleBlock = (section.match(/## Título visual\n+```text\n([\s\S]*?)```/) || [])[1] || '';
  const body = extractField(section, 'Texto introdutório') || cleanLines(
    (section.match(/## Texto introdutório\n+([\s\S]*?)(?=\n## )/) || [])[1] || '',
  ).join(' ');

  return {
    titleLines: titleBlock.split('\n').map((line) => line.trim()).filter(Boolean),
    body: body.replace(/\s+/g, ' ').trim(),
  };
}

function parseClosing(section) {
  const groups = [...section.matchAll(/## Grupo textual[^\n]*\n[\s\S]*?- Título textual:\n+\n> ([^\n]+)/g)]
    .map((match) => match[1].trim());

  return {
    meta: {
      weekday: 'DEZEMBRO',
      day: 'DE',
      month: '',
      year: '2026',
    },
    groups,
  };
}

export function flattenFragments(chapter) {
  const fragments = [];
  chapter.blocks.forEach((block, blockIndex) => {
    block.fragments.forEach((fragment) => {
      fragments.push({
        ...fragment,
        blockIndex,
        role: block.role,
        index: fragments.length,
      });
    });
  });
  return fragments;
}

export function parseEditorial(markdown) {
  const sections = markdown.split(/\n(?=# )/).map((section) => section.trim()).filter(Boolean);
  const chapters = [];
  let closing = null;

  sections.forEach((section) => {
    const heading = section.split('\n')[0];
    if (!heading.startsWith('# Capítulo ')) return;
    if (heading.includes('06_cap04')) return;
    if (heading.includes('12_cap10') || heading.includes('fechamento')) {
      closing = parseClosing(section);
      return;
    }
    chapters.push(parseChapter(section, chapters.length));
  });

  return {
    chapters,
    intro: parseIntro(markdown),
    closing,
  };
}
